import { TypedDocumentNode } from '@apollo/client';
import {
  MultiSelectOption,
  SelectOption,
  ServerSideMultiSelect,
  ServerSideMultiSelectProps,
} from '@otp/ui';
import {
  BaseFilter,
  BasePagination,
  BaseQueryProps,
  BaseSorting,
} from '@otp/ui/src/types/types';
import { DocumentNode } from 'graphql';
import React, { useEffect, useMemo } from 'react';
import {
  PaginationInput,
  StoreFilterInput,
  StoreModel,
  StoresQuery,
} from 'src/graphql/generated/graphql';
import { useStoresLazy } from 'src/hooks/query/stores/use-stores-lazy';

type StoresProps<
  TOption extends SelectOption | MultiSelectOption,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
  TData,
  TFilter extends BaseFilter,
  TPagination extends BasePagination,
  TSorting extends BaseSorting,
  TVariables extends BaseQueryProps<TQuery, TFilter, TPagination, TSorting>,
  TQuery extends DocumentNode | TypedDocumentNode<TData, TVariables>,
> = {
  className?: string;
  value?: TOption;
  multiple: Multiple;
  storeMap?: (item: Partial<StoreModel>) => TOption;
  filterGenerator?: (
    value: Partial<StoreModel> | Partial<StoreModel>[] | any,
  ) => {
    filter: StoreFilterInput;
    pagination: PaginationInput;
  };
  useCheckboxOption?: boolean;
} & ServerSideMultiSelectProps<
  TOption,
  Multiple,
  DisableClearable,
  FreeSolo,
  TData,
  TFilter,
  TPagination,
  TSorting,
  TVariables,
  TQuery
>;

export const StoresServerSideField = <
  TOption extends SelectOption | MultiSelectOption,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
  TData,
  TFilter extends BaseFilter,
  TPagination extends BasePagination,
  TSorting extends BaseSorting,
  TVariables extends BaseQueryProps<TQuery, TFilter, TPagination, TSorting>,
  TQuery extends DocumentNode | TypedDocumentNode<TData, TVariables>,
>({
  className,
  disabled,
  useCheckboxOption,
  value,
  multiple,
  onChange,
  storeMap = (store) => {
    return {
      value: store?.id,
      name: store?.nationalStoreNumber || store?.name || store?.id,
    } as TOption;
  },
  filterGenerator = (value: any) => {
    const fieldValue = Array.isArray(value) ? value : [value];

    return {
      filter: {
        ids: fieldValue.filter((value) => !value),
      },
      pagination: {
        limit: fieldValue.length,
        offset: 0,
      },
    };
  },
  ...rest
}: StoresProps<
  TOption,
  Multiple,
  DisableClearable,
  FreeSolo,
  TData,
  TFilter,
  TPagination,
  TSorting,
  TVariables,
  TQuery
>) => {
  const [fetchStores, { data, loading }] = useStoresLazy();

  useEffect(() => {
    if (value) {
      const filter = filterGenerator(value.value);

      fetchStores({
        variables: filter,
      });
    }
  }, []);
  const defaultData = useMemo(
    () => data?.stores.records?.map(storeMap),
    [data],
  );

  return (
    <>
      {!loading && (
        <ServerSideMultiSelect
          {...rest}
          onChange={onChange}
          multiple={multiple}
          options={defaultData || []}
          lazyQuery={useStoresLazy}
          loading={loading}
          fullWidth={true}
          className={className}
          disabled={disabled}
          useCheckboxOption={useCheckboxOption}
          value={value}
          parseData={(data: StoresQuery, page: number, limit: number) => {
            return {
              results: data.stores.records?.map(storeMap) || [],
              hasNextPage: data.stores.pagination.totalCount > page * limit,
            };
          }}
        ></ServerSideMultiSelect>
      )}
    </>
  );
};
